<template>
  <div class="service-groups-input" :class="{ 'service-groups-input--active-menu': isMenuOpen }">
    <div v-if="value.length" class="services-list">
      <e-chip 
        v-for="(serviceCid, index) in value" 
        :key="index" 
        :closable = canRemoveService(serviceCid)
        class="chip-item"
        @click:close="removeService(serviceCid)"
      >
        <div class="service-item">
          <span>{{ getServiceName(serviceCid) }}</span>
          <img 
            v-if="isServiceFirst(serviceCid)" 
            :src="firstServiceIcon"
            alt="Первая услуга"
            title="Первая услуга"
          />
          <img 
            v-if="isServiceLast(serviceCid)" 
            :src="lastServiceIcon"
            alt="Последняя услуга"
            title="Последняя услуга"
          />
        </div>
      </e-chip>
    </div>

    <div v-if="value.length" class="paragraph-xs text-secondary mt-4 mb-4 d-flex align-center">
      <div class="d-flex align-center ga-1">
        <img 
          :src="firstServiceIcon"
          alt="Первая услуга"
          title="Первая услуга"
        />
        <span>Первая оказываемая услуга</span>
      </div>
      <div class="d-flex align-center ga-1 ml-4">
        <img 
          :src="lastServiceIcon"
          alt="Последняя услуга"
          title="Последняя услуга"
        />
        <span>Последняя оказываемая услуга</span>
      </div>
    </div>

    <e-input
      v-model="searchValue"
      label="Поиск услуги"
      :error-messages="servicesError"
      @focus="onFocus"
      @keydown.enter.stop.prevent="onEnter"
    >
      <template #prepend-inner>
        <e-icon name="search" class="text-secondary" size="20" />
      </template>
      <template #append-inner>
        <e-icon name="chevronDown" class="text-secondary" size="20" />
      </template>
      <template #default>
        <v-menu
          v-if="suggestedServices.loaded"
          v-model="isMenuOpen"
          activator="parent"
          attach
          :open-on-click="false"
          :open-on-focus="false"
          :close-on-content-click="false"
          :content-class="['service-groups-input__content']"
          :max-height="$vuetify.display.smAndDown ? 200 : 360"
        >
          <v-list>
            <div v-for="(item, index) in filteredItems" :key="index">
              <v-list-item v-for="service in item.services" :key="service.cid" @click="addService(service.cid)">
                <div class="d-flex align-center">
                  <e-icon
                    name="check"
                    class="service-groups-input__check text-link mr-2"
                    size="20"
                    :class="{ shown: value.includes(service.cid) }"
                  />
                  <div class="input-text" v-html="highlightMatch(service.shortName, searchValue)" />
                </div>
              </v-list-item>
              <divider v-if="index < filteredItems.length - 1" class="my-2 mx-4" />
            </div>
          </v-list>
        </v-menu>
      </template>
    </e-input>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, toRef, onMounted } from 'vue';
import Divider from '@/core/components/helpers/Divider.vue';
import { EChip, EIcon, EInput } from '@/core/components/ui';
import {
  type ServiceGroupSuggested,
  type SuggestedService,
  useServiceGroupsStore,
} from '@/parts/tariffs/store/serviceGroups';
import firstServiceIcon from '@/assets/img/pages/tariffs/service-groups/first-service.svg';
import lastServiceIcon from '@/assets/img/pages/tariffs/service-groups/last-service.svg';

const props = defineProps<{
  errorMessages?: string | string[];
  tariffScheduleCid?: string;
  hotelSetCid?: string;
}>();

const value = defineModel<string[]>({ required: true });

const { getSuggestedServices, suggestedServices, calculateServiceSequence } = useServiceGroupsStore();

const searchValue = ref('');
const isMenuOpen = ref(false);

const servicesFlags = ref<Array<{ cid: string; isFirst: boolean; isLast: boolean; shortName: string, parentCid?: string | null;}>>([]);

const onFocus = () => {
  isMenuOpen.value = true;
};

const serviceGroups = toRef(() => (suggestedServices.value.loaded ? suggestedServices.value.data : []));

const allServices = computed(() =>
  serviceGroups.value.reduce<(SuggestedService & { parent: number })[]>(
    (acc, item, index) => [...acc, ...item.services.map((service) => ({ ...service, parent: index }))],
    [],
  ),
);

const isServiceFirst = (cid: string) => {
  const flag = servicesFlags.value.find(f => f.cid === cid);
  return flag?.isFirst || false;
};

const isServiceLast = (cid: string) => {
  const flag = servicesFlags.value.find(f => f.cid === cid);
  return flag?.isLast || false;
};

const canRemoveService = (cid: string) => {
  const flag = servicesFlags.value.find(f => f.cid === cid);
  return !flag?.parentCid;
};

const getServiceName = (cid: string) => {
  const flagged = servicesFlags.value.find(f => f.cid === cid);
  if (flagged) return flagged.shortName;
  
  const service = allServices.value.find(s => s.cid === cid);
  return service?.shortName || cid;
};

const recalculateServicesSequence = async (serviceCids: string[]) => {
  if (!props.hotelSetCid) return;
  
  const result = await calculateServiceSequence({
    tariffScheduleCid: props.tariffScheduleCid,
    hotelSetCid: props.hotelSetCid,
    serviceCids: serviceCids,
  });
  
  if (!result?.data?.calculateServiceSequence) return;
  
  const responseServices = result.data.calculateServiceSequence;
  
  const sortedServiceCids = responseServices.map(s => s.cid);
  
  const isIdentical = sortedServiceCids.length === value.value.length && 
                      sortedServiceCids.every((val, index) => val === value.value[index]);
  
  if (!isIdentical) {
    value.value = sortedServiceCids;
  }
  
  servicesFlags.value = responseServices.map(service => ({
    cid: service.cid,
    parentCid: service.parentCid,
    isFirst: service.isFirst,
    isLast: service.isLast,
    shortName: service.shortName,
  }));
};

const addService = async (service: string) => {
  if (value.value.includes(service)) {
    await recalculateServicesSequence(value.value.filter((s) => s !== service));
    return;
  }

  const serviceData = allServices.value.find((s) => s.cid === service);

  if (!serviceData) {
    return;
  }

  let currentValue = [...value.value];

  currentValue = currentValue.filter((x) => {
    const foundService = allServices.value.find((s) => s.cid === x);
    return !foundService || foundService.parent !== serviceData.parent;
  });

  await recalculateServicesSequence([...currentValue, service]);
};

const onEnter = () => {
  addService(searchValue.value);
};

const removeService = async (service: string) => {
  await recalculateServicesSequence(value.value.filter((s) => s !== service));
};

const highlightMatch = (text: string, query: string) => {
  if (!query) return text;
  const regex = new RegExp(`(${query})`, 'gi');
  return text.replace(regex, '<span class="service-groups-input__highlight">$1</span>');
};

const filteredItems = computed(() =>
  serviceGroups.value.reduce<ServiceGroupSuggested>((acc, serviceGroup) => {
    const sg = { ...serviceGroup };

    if (searchValue.value) {
      sg.services = sg.services.filter((service) =>
        service.shortName.toLowerCase().includes(searchValue.value.toLowerCase()),
      );
    }

    if (sg.services.length) {
      acc.push(sg);
    }

    return acc;
  }, []),
);

const servicesError = computed(() => {
  let hasError = false;

  value.value.forEach((s) => {
    if (!hasError) {
      hasError = !allServices.value.some((service) => service.cid === s);
    }
  });

  return hasError ? 'Обратитесь в техническую Поддержку, если не можете найти необходимые услуги' : '';
});

onMounted(async () => {
  if (value.value.length) {
    await recalculateServicesSequence(value.value);
  }
});

await getSuggestedServices();
</script>

<style scoped lang="scss">
.service-groups-input {
  &__check {
    opacity: 0;
    transition: opacity $transition-duration;

    &.shown {
      opacity: 1;
    }
  }

  &:global(.service-groups-input__highlight) {
    color: $primary-color;
  }
}

.services-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  margin-bottom: 16px;
}

.service-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.chip-item {
  width: fit-content;
}
</style>
